// Which resolution object the report modal's RESOLUTION section renders for the
// currently-selected resolution entry.
//
// Three cases, and the distinction between the first two is what this exists for:
//
//   1. The entry carries a snapshot WITH a report body → render it. (A reconciled
//      newest attempt whose body arrived inline.)
//   2. The entry is the TAIL (newest attempt) and has no usable body → return
//      `null`, deliberately. The modal's `scrubResolutionSnapshot ||
//      liveSource.resolution` then falls through to the pointer-fetched resolution
//      that `applyPointerOverrides` merged onto the live source — the full report
//      from /api/resolve/report, which serves the latest conclusive attempt and so
//      describes exactly this entry.
//   3. An OLDER attempt with no body → a status-only object built from the entry's
//      OWN status and moment. Never fall through here: the fetched resolution is
//      the LATEST conclusive attempt, so falling through would caption an older
//      attempt's slide with a newer attempt's rationale and evidence.
//
// Case 2 is the bug this module fixes. `buildReforecastEntries` reconciles the
// newest resolution entry against `active.resolution`, and the wire copy of that
// is THIN — `{status, resolved_at, resolution_date}`, no `report` (the notifier's
// resolved path stamps the verdict and dates only). A thin snapshot is truthy, so
// it used to win the fall-through race and shadow the already-fetched full report:
// a resolved contract rendered "DID NOT OCCUR" plus a date, and dropped 2,000+
// characters of rationale and every evidence link that were sitting in props one
// line away. Verdict-and-dates is not a report body; `resolutionHasBody` draws
// that line and case 2 restores the intended fall-through.

import { parseResolveDateLocal } from "../formatDates.js";
import { resolutionHasBody } from "./resolutionBody.js";

/**
 * The LOCAL calendar date (`YYYY-MM-DD`) of a resolution moment.
 *
 * NOT `String(moment).slice(0, 10)`: for a full ISO instant that slices the **UTC**
 * date, which is off by one west of UTC — `"2026-07-20T01:00:00Z"` is still July 19th
 * in Los Angeles. Resolution moments are placed on the timeline as LOCAL instants
 * (parseResolveDateLocal), so the date shown on a slide has to be the local date of
 * the same instant or the chip disagrees with the marker the user clicked.
 *
 * A date-only `"YYYY-MM-DD"` parses to local midnight and round-trips unchanged.
 * An unparseable moment falls back to the raw leading 10 characters — best-effort,
 * same tolerance as the formatters.
 */
function localYmd(moment) {
  const d = parseResolveDateLocal(moment);
  if (!d || isNaN(d.getTime())) return String(moment).slice(0, 10);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Pick the resolution snapshot for the selected resolution entry, or `null` to let
 * the caller fall back to the pointer-fetched resolution overlay.
 *
 * @param {{status?:string, moment?:string, snapshot?:object|null}|null} entry
 *   the selected `kind:'resolution'` entry (buildReforecastEntries), or null when
 *   the current position is not a resolution
 * @param {boolean} isTail whether `entry` is the NEWEST resolution (the list tail) —
 *   only the tail may fall through, because the read bridge serves the latest
 *   conclusive attempt only
 * @returns {object|null} the snapshot to render, or null to fall through
 */
export function selectResolutionSnapshot(entry, isTail) {
  if (!entry) return null;
  // A snapshot only counts when it can actually fill the section.
  if (resolutionHasBody(entry.snapshot)) return entry.snapshot;
  // Newest attempt → fall through to the fetched full report (case 2).
  if (isTail) return null;
  // Older attempt → its own verdict + date, body unavailable (case 3). The modal
  // labels the missing body rather than borrowing the newest attempt's.
  return {
    status: entry.status,
    resolved_at: entry.moment,
    // The "RESOLVED ON" chip is gated on resolution_date; derive the date part
    // from the entry moment so an older attempt's slide shows its own date — as
    // the LOCAL date of that instant, matching where the timeline placed it.
    resolution_date: localYmd(entry.moment),
  };
}
